import { api, acct } from "./client";
import type { Patch } from "./crud";
import type { ListQuery, UserSummary } from "./types";

/** "First Last", else the username, else the id: how every screen labels a user. */
export const fullName = (u: Pick<UserSummary, "first_name" | "last_name" | "username" | "id">) =>
  `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() || u.username || u.id;

/** One strategy block of `call_forward` (also the shape of its `unconditional`, `busy`, `no_answer` children). */
export interface CallForwardStrategy {
  enabled?: boolean;
  number?: string;
  keep_caller_id?: boolean;
  direct_calls_only?: boolean;
  require_keypress?: boolean;
  ignore_early_media?: boolean;
}
export interface CallForward extends CallForwardStrategy {
  substitute?: boolean;
  failover?: boolean;
  unconditional?: CallForwardStrategy;
  busy?: CallForwardStrategy;
  no_answer?: CallForwardStrategy;
}

/** `call_recording.parameters` in the crossbar schema. */
export interface CallRecordingParams {
  enabled?: boolean;
  format?: "mp3" | "wav";
  time_limit?: number;
  url?: string;
  record_on_answer?: boolean;
  record_on_bridge?: boolean;
  record_min_sec?: number;
  should_announce_when_recording?: boolean;
}
export type CallRecordingDirection = "inbound" | "outbound";
export type CallRecordingNetwork = "onnet" | "offnet";
export type CallRecording = Partial<Record<CallRecordingDirection | "any", Partial<Record<CallRecordingNetwork | "any", CallRecordingParams>>>>;

/** One `smartpbx.<feature>` entry. */
export interface SmartPbxFeature {
  enabled?: boolean;
}
/** The features the ring group screen toggles on a group document. */
export type GroupFeatureKey = "call_recording" | "forward" | "prepend" | "ringback" | "next_action";
/**
 * monster-ui's bookkeeping object on user and group documents; the console
 * keeps writing it so both UIs agree. Users get the five named keys, groups
 * the `GroupFeatureKey` ones (`call_recording` is shared).
 */
export interface SmartPbx extends Partial<Record<GroupFeatureKey, SmartPbxFeature>> {
  call_forwarding?: SmartPbxFeature & { default?: string; unconditional?: { voicemail?: string }; busy?: { voicemail?: string }; no_answer?: { voicemail?: string } };
  faxing?: SmartPbxFeature;
  conferencing?: SmartPbxFeature;
  find_me_follow_me?: SmartPbxFeature;
}

export interface UserDoc extends UserSummary {
  caller_id?: Record<string, { name?: string; number?: string }>;
  caller_id_options?: { outbound_privacy?: "none" | "number" | "name" | "full" };
  call_forward?: CallForward;
  call_failover?: { enabled?: boolean; number?: string; require_keypress?: boolean; direct_calls_only?: boolean; keep_caller_id?: boolean };
  hotdesk?: { enabled?: boolean; id?: string; pin?: string; require_pin?: boolean; keep_logged_in_elsewhere?: boolean };
  do_not_disturb?: { enabled?: boolean };
  call_recording?: CallRecording;
  /** Legacy flag; `call_recording` is what kazoo reads now. */
  record_call?: { enabled?: boolean };
  vm_to_email_enabled?: boolean;
  music_on_hold?: { media_id?: string };
  call_restriction?: Record<string, { action?: "allow" | "deny" }>;
  directories?: Record<string, string>;
  smartpbx?: SmartPbx;
  timezone?: string;
  language?: string;
  require_password_update?: boolean;
  password?: string;
  ui_metadata?: { ui?: string; version?: string; origin?: string };
  [key: string]: unknown;
}

/** What `DELETE .../users/{id}` can cascade to (crossbar's "enhanced user deletion"). */
export type UserDeleteObjectType = "phone_numbers" | "callflow" | "vmbox" | "device" | "conference" | "faxbox" | "media";
export interface UserDeleteResult {
  id?: string;
  object_types?: { type: string; id: string; status: string; error_code?: string; error_msg?: string }[];
  [key: string]: unknown;
}

export const users = {
  list: (accountId: string, q?: ListQuery) => api.list<UserSummary>(acct(accountId, "users"), { paginate: false, ...q }),
  get: (accountId: string, id: string) => api.get<UserDoc>(acct(accountId, "users", id)),
  /** `sendEmail: false` stops the `new_user` mail (which carries the password in clear); the server default is to send it. */
  create: (accountId: string, doc: Partial<UserDoc>, acceptCharges = false, opts: { sendEmail?: boolean } = {}) =>
    api.put<UserDoc>(acct(accountId, "users"), doc, { acceptCharges, query: { send_email_on_creation: opts.sendEmail === undefined ? undefined : opts.sendEmail ? "true" : "false" } }),
  /** Full replace (POST). Prefer `patch` for partial edits. */
  update: (accountId: string, id: string, doc: UserDoc) => api.post<UserDoc>(acct(accountId, "users", id), doc),
  patch: (accountId: string, id: string, patch: Patch<UserDoc>) => api.patch<UserDoc>(acct(accountId, "users", id), patch),
  /**
   * Delete the user. With `objectTypes` crossbar also deletes the listed
   * objects the user owns and reports each one in `object_types`.
   */
  remove: (accountId: string, id: string, objectTypes?: UserDeleteObjectType[]) =>
    api.delete<UserDeleteResult>(acct(accountId, "users", id), objectTypes?.length ? { body: { object_types: objectTypes } } : undefined),
  /** `POST .../users/{id}/presence {action: set, state}`; DND uses `confirmed` / `terminated`. */
  setPresence: (accountId: string, id: string, state: "confirmed" | "terminated" | "early") =>
    api.post(acct(accountId, "users", id, "presence"), { action: "set", state }),
  /** PNG of the TOTP enrolment QR code. */
  qrcodeUrl: (accountId: string, id: string) => `/v2${acct(accountId, "users", id, "qrcode")}`,
};
